import opentype, { BoundingBox, Path } from "opentype.js";
import computeLayout from "opentype-layout";
//#region src/css-color.ts
const NAMED_COLORS = {
	aqua: "#0ff",
	beige: "#f5f5dc",
	black: "#000",
	blue: "#00f",
	brown: "#a52a2a",
	coral: "#ff7f50",
	crimson: "#dc143c",
	cyan: "#0ff",
	darkblue: "#00008b",
	darkgray: "#a9a9a9",
	darkgreen: "#006400",
	darkgrey: "#a9a9a9",
	darkred: "#8b0000",
	darkviolet: "#9400d3",
	fuchsia: "#f0f",
	gold: "#ffd700",
	gray: "#808080",
	green: "#008000",
	grey: "#808080",
	indigo: "#4b0082",
	khaki: "#f0e68c",
	lightblue: "#add8e6",
	lightgray: "#d3d3d3",
	lightgreen: "#90ee90",
	lightgrey: "#d3d3d3",
	lightyellow: "#ffffe0",
	lime: "#0f0",
	magenta: "#f0f",
	maroon: "#800000",
	navy: "#000080",
	olive: "#808000",
	orange: "#ffa500",
	pink: "#ffc0cb",
	purple: "#800080",
	red: "#f00",
	salmon: "#fa8072",
	silver: "#c0c0c0",
	tan: "#d2b48c",
	teal: "#008080",
	turquoise: "#40e0d0",
	violet: "#ee82ee",
	white: "#fff",
	yellow: "#ff0"
};
function toSrgb(cssColor) {
	if (!cssColor.startsWith("#")) cssColor = NAMED_COLORS[cssColor] || "";
	if (cssColor.startsWith("#")) switch (cssColor.length) {
		case 4: return [
			17 * parseInt(cssColor[1], 16),
			17 * parseInt(cssColor[2], 16),
			17 * parseInt(cssColor[3], 16)
		];
		case 7: return [
			parseInt(cssColor.slice(1, 3), 16),
			parseInt(cssColor.slice(3, 5), 16),
			parseInt(cssColor.slice(5, 7), 16)
		];
	}
	return null;
}
function applyColorOpacity(cssColor, opacityFactor) {
	if (isNaN(opacityFactor) || opacityFactor >= 1 || cssColor === "transparent") return cssColor;
	if (opacityFactor < 0) opacityFactor = 0;
	const rgb = toSrgb(cssColor);
	if (rgb) return `rgba(${rgb[0]},${rgb[1]},${rgb[2]},${opacityFactor})`;
	return `color(from ${cssColor} xyz-d65 x y z / calc(alpha * ${opacityFactor}))`;
}
//#endregion
//#region src/unit-system-table.ts
function generateUnitSystemTable(fuPerEm, pxPerEm, pxPerIn) {
	return {
		Q: {
			fu: 5 * pxPerIn * fuPerEm / (508 * pxPerEm),
			px: 5 * pxPerIn / 508
		},
		cm: {
			fu: 50 * pxPerIn * fuPerEm / (127 * pxPerEm),
			px: 50 * pxPerIn / 127
		},
		em: {
			fu: fuPerEm,
			px: pxPerEm
		},
		fu: {
			fu: 1,
			px: pxPerEm / fuPerEm
		},
		in: {
			fu: pxPerIn * fuPerEm / pxPerEm,
			px: pxPerIn
		},
		mm: {
			fu: 5 * pxPerIn * fuPerEm / (127 * pxPerEm),
			px: 5 * pxPerIn / 127
		},
		pc: {
			fu: pxPerIn * fuPerEm / (6 * pxPerEm),
			px: pxPerIn / 6
		},
		pt: {
			fu: pxPerIn * fuPerEm / (72 * pxPerEm),
			px: pxPerIn / 72
		},
		px: {
			fu: fuPerEm / pxPerEm,
			px: 1
		}
	};
}
//#endregion
//#region src/unit-system.ts
function assertUnits(unitValue, expectedUnit) {
	if (unitValue.unit !== expectedUnit) throw new Error(`Units must be ${expectedUnit}`);
	return unitValue;
}
function assertPixels(unitValue) {
	return assertUnits(unitValue, "px");
}
function assertFontUnits(unitValue) {
	return assertUnits(unitValue, "fu");
}
function badOverload1d() {
	return /* @__PURE__ */ new Error("Invalid arguments. Specify a single UnitValue argument, or a number and a unit string");
}
function badOverload2d() {
	return /* @__PURE__ */ new Error("Invalid arguments. Specify a single [UnitValue, UnitValue] argument, or a [number, number] and a unit string");
}
function parseOverload(uvOrQ, unit) {
	if (typeof uvOrQ === "number" && typeof unit === "string") return {
		value: uvOrQ,
		unit
	};
	if (typeof uvOrQ === "object" && unit == null) return uvOrQ;
	throw badOverload1d();
}
var UnitSystem = class {
	constructor(fontUnitsPerEm, pxPerEm = 16, pxPerIn = 96) {
		this.fontUnitsPerEm = fontUnitsPerEm;
		this.pxPerEm = pxPerEm;
		this.pxPerIn = pxPerIn;
		this.table = generateUnitSystemTable(fontUnitsPerEm, pxPerEm, pxPerIn);
	}
	getFactor(fromUnit, toUnit) {
		const row = this.table[fromUnit];
		if (!row) throw new Error("Unknown input unit");
		const factor = row[toUnit];
		if (factor == null) throw new Error("Unknown output unit");
		return factor;
	}
	toPixels(uvOrQ, unit) {
		return assertPixels(this.convert(parseOverload(uvOrQ, unit), "px"));
	}
	toFontUnits(uvOrQ, unit) {
		return assertFontUnits(this.convert(parseOverload(uvOrQ, unit), "fu"));
	}
	toPixels2D([x, y], unit) {
		if (unit == null && x && y && typeof x === "object" && typeof y === "object") return [this.toPixels(x), this.toPixels(y)];
		if (typeof unit === "string" && typeof x === "number" && typeof y === "number") return [this.toPixels(x, unit), this.toPixels(y, unit)];
		throw badOverload2d();
	}
	convert({ value, unit }, toUnit) {
		return {
			value: value * this.getFactor(unit, toUnit),
			unit: toUnit
		};
	}
};
function px(value) {
	return {
		value,
		unit: "px"
	};
}
//#endregion
//#region src/unit-value.ts
const CSS_DIMENSION_TOKEN = /^([+-]?(?:\d*\.\d+|\d+)(?:e[+-]?\d+)?)([%a-z]*)$/i;
/**
* Parses a CSS dimension into its numeric value and unit of measurement.
*
* Invalid strings return undefined. This includes non-zero quantities
* without units.
*/
function parseUnitValue(text) {
	const match = CSS_DIMENSION_TOKEN.exec(text.trim());
	if (!match) return void 0;
	const value = +match[1];
	const unit = match[2] || "";
	if (!unit && value !== 0) return void 0;
	return {
		value,
		unit
	};
}
//#endregion
//#region src/defaults.ts
/**
* Parses a CSS unit string, with some extra affordances.
*
* If the argument is nullish, return undefined.
* If the argument is a number, assume the unit 'px'.
* If the argument is a numeric string without a unit, also assume 'px'.
* Otherwise, parse the string as a CSS <dimension>.
*/
function unitOrPx(u) {
	if (u == null || u === "") return void 0;
	if (typeof u === "number") return px(u);
	const testToNum = +u;
	if (isFinite(testToNum)) return px(testToNum);
	return parseUnitValue(u);
}
/**
* Discards a unit (by returning undefined) whose value is zero.
*/
function nonZero(uv) {
	if (!uv || uv.value === 0) return void 0;
	return uv;
}
/**
* Resolves percentage units against an optional reference value.
*
* If the provided unit is not a percentage, returns it unmodified.
*/
function resolvePercent(uv, reference) {
	if ((uv === null || uv === void 0 ? void 0 : uv.unit) === "%") {
		if (reference) return {
			value: uv.value / 100 * reference.value,
			unit: reference.unit
		};
		return;
	}
	return uv;
}
function resolvePosition(inLeft, inTop) {
	const left = unitOrPx(inLeft);
	const top = unitOrPx(inTop);
	return left || top ? {
		left,
		top
	} : void 0;
}
/**
* Returns the maximum valid value, converted to pixels according to the
* UnitSystem argument.
*
* Resolves numbers and unitless strings according to `unitOrPx`.
*/
function maxValidPx(unitSystem, ...unparsedArgs) {
	const max = px(0);
	for (const arg of unparsedArgs) if (arg != null && arg != "") {
		const parsedArg = unitOrPx(arg);
		if (parsedArg) {
			const convArg = unitSystem.toPixels(parsedArg);
			if (convArg.value > max.value) max.value = convArg.value;
		}
	}
	return max;
}
function resolveDropshadow(unitSystem, dropshadowColor, dropshadowBlur, dropshadowBlurX, dropshadowBlurY, dropshadowOffsetX, dropshadowOffsetY, dropshadowOpacity) {
	if (dropshadowOpacity == null || dropshadowOpacity === "") return void 0;
	let opacity = +(dropshadowOpacity !== null && dropshadowOpacity !== void 0 ? dropshadowOpacity : 0);
	if (isNaN(opacity)) return void 0;
	if (opacity > 1) opacity = 1;
	if (opacity < 0) opacity = 0;
	const color = applyColorOpacity(dropshadowColor || "black", opacity);
	const blur = maxValidPx(unitSystem, dropshadowBlur, dropshadowBlurX, dropshadowBlurY);
	const offsetX = unitOrPx(dropshadowOffsetX) || px(0);
	const offsetY = unitOrPx(dropshadowOffsetY) || px(0);
	return {
		color,
		blur,
		offset: unitSystem.toPixels2D([offsetX, offsetY])
	};
}
function resolveStroke(unitSystem, strokeColor, strokeLinecap, strokeWidth, strokeOpacity) {
	const width = unitOrPx(strokeWidth);
	if (!width) return void 0;
	let opacity = +(strokeOpacity !== null && strokeOpacity !== void 0 ? strokeOpacity : 1);
	if (isNaN(opacity) || opacity > 1) opacity = 1;
	if (opacity < 0) opacity = 0;
	return {
		color: strokeColor || "black",
		opacity,
		width: unitSystem.toPixels(width),
		linecap: strokeLinecap || "round"
	};
}
function resolveSizeAxis(unitSystem, inLength, containerLength) {
	const inLengthUnits = unitOrPx(inLength);
	if (!inLengthUnits) return void 0;
	const finiteLength = resolvePercent(inLengthUnits, containerLength == null ? void 0 : px(containerLength));
	if (!finiteLength) return void 0;
	if (finiteLength.value === 0) return void 0;
	return unitSystem.toPixels(finiteLength);
}
function resolveSize(unitSystem, inWidth, inHeight, container) {
	return {
		width: resolveSizeAxis(unitSystem, inWidth, container === null || container === void 0 ? void 0 : container.offsetWidth),
		height: resolveSizeAxis(unitSystem, inHeight, container === null || container === void 0 ? void 0 : container.offsetHeight)
	};
}
function resolveStyles(inStyles, font, container) {
	const fontSizeAnyUnit = nonZero(unitOrPx(inStyles.fontSize)) || px(12);
	const fontSize = new UnitSystem(font.unitsPerEm).toPixels(fontSizeAnyUnit);
	const unitSystem = new UnitSystem(font.unitsPerEm, fontSize.value);
	const letterSpacing = nonZero(unitOrPx(inStyles.letterSpacing)) || px(0);
	const lineHeight = unitOrPx(inStyles.lineHeight) || { ...fontSize };
	const position = resolvePosition(inStyles.left, inStyles.top);
	const offsetX = unitOrPx(inStyles.offsetX) || px(0);
	const offsetY = unitOrPx(inStyles.offsetY) || px(0);
	const { width, height } = resolveSize(unitSystem, inStyles.width, inStyles.height, container);
	let boundingBox = "";
	if (typeof inStyles.boundingBox === "string") boundingBox = inStyles.boundingBox;
	else if (inStyles.boundingBox) boundingBox = "1px dashed red";
	const dropshadow = resolveDropshadow(unitSystem, inStyles.dropshadowColor, inStyles.dropshadowBlur, inStyles.dropshadowBlurX, inStyles.dropshadowBlurY, inStyles.dropshadowOffsetX, inStyles.dropshadowOffsetY, inStyles.dropshadowOpacity);
	const stroke = resolveStroke(unitSystem, inStyles.strokeColor, inStyles.strokeLinecap, inStyles.strokeWidth, inStyles.strokeOpacity);
	return {
		color: inStyles.color || "black",
		fontSize,
		letterSpacing,
		lineHeight,
		textAlign: inStyles.textAlign || "left",
		verticalAlign: inStyles.verticalAlign || "top",
		position,
		offset: [offsetX, offsetY],
		width,
		height,
		boundingBox,
		dropshadow,
		stroke,
		maskRows: !!inStyles.maskRows
	};
}
//#endregion
//#region src/markup.ts
const XMLNS_SVG = "http://www.w3.org/2000/svg";
/**
* Compact a string containing numbers, if possible.
*/
function minifyNumbers(text) {
	return text.replace(/(^|[\d.])\d*\.\d+/g, (fractionalPart) => {
		let cursor = fractionalPart.length - 1;
		while (fractionalPart[cursor] === "0") cursor--;
		if (fractionalPart[cursor] === ".") cursor--;
		return fractionalPart.slice(0, cursor + 1);
	});
}
const CSS_PROPERTY_ORDER_OVERRIDE = [
	"left",
	"top",
	"right",
	"bottom",
	"width",
	"height"
];
function cssPropSortKey(propName) {
	const overrideIndex = CSS_PROPERTY_ORDER_OVERRIDE.indexOf(propName);
	if (overrideIndex !== -1) return `${`${overrideIndex}`.padStart(2, "0")}${propName}`;
	return `99${propName}`;
}
function orderCssPropPairs([aName, aValue], [bName, bValue]) {
	const aKey = cssPropSortKey(aName) + ` ${aValue}`;
	const bKey = cssPropSortKey(bName) + ` ${bValue}`;
	return aKey.localeCompare(bKey);
}
function escapeCssTerminators(text) {
	return text.replace(/[\x00-\x1F\x7F;{}\\'`]/g, (m) => {
		const cp = m.codePointAt(0);
		if (cp == null) return "";
		const hexDigits = cp.toString(16);
		return `\\${hexDigits}${hexDigits.length < 6 ? " " : ""}`;
	});
}
function inlineStylesAttr(styles, sort) {
	const pairs = Object.entries(styles);
	if (sort) pairs.sort(orderCssPropPairs);
	return pairs.map(([key, value]) => `${key}:${escapeCssTerminators(value)};`).join("");
}
function normalizeWhitespace(text) {
	return text.split(/\r?\n|\r/).map((line) => line.trim().replace(/[\t ]+/g, " ")).join("\n");
}
//#endregion
//#region src/ot-utils.ts
/**
* Helpers and adapters for stuff in `opentype.js` and `opentype-layout`
*/
function computeLayoutWithUnits(font, text, options) {
	const { unitSystem, width, letterSpacing, lineHeight, ...passthru } = options;
	const maybeToFontUnits = (uv) => uv == null ? void 0 : unitSystem.toFontUnits(uv).value;
	return computeLayout(font, text, {
		width: maybeToFontUnits(width),
		letterSpacing: maybeToFontUnits(letterSpacing),
		lineHeight: maybeToFontUnits(lineHeight),
		...passthru
	});
}
/**
* Grow a BoundingBox to a size that contains both it and the operand.
*
* If the BoundingBox is empty, it becomes equal to the operand.
*/
function boundingBoxUnion(target, operand) {
	target.addPoint(operand.x1, operand.y1);
	target.addPoint(operand.x2, operand.y2);
}
/**
* Enlarge a bounding box by offsetting its edges by the given amount.
*/
function boundingBoxOutset(target, addend) {
	target.x1 -= addend;
	target.y1 -= addend;
	target.x2 += addend;
	target.y2 += addend;
}
/**
* Round the coordinates of a BoundingBox to the next integer in the
* direction that produces a larger BoundingBox.  For minimum edges, this is
* a Math.floor. For maximum edges a Math.ceil.
*/
function boundingBoxRoundOutset(target) {
	target.x1 = Math.floor(target.x1);
	target.y1 = Math.floor(target.y1);
	target.x2 = Math.ceil(target.x2);
	target.y2 = Math.ceil(target.y2);
}
/**
* Return the x, y, width and height of a BoundingBox.
*
* The returned x, y is the minimum corner.
*/
function boundingBoxGetXYWH(query) {
	const left = Math.min(query.x1, query.x2);
	const right = Math.max(query.x1, query.x2);
	const top = Math.min(query.y1, query.y2);
	const bottom = Math.max(query.y1, query.y2);
	return {
		x: left,
		y: top,
		width: right - left,
		height: bottom - top
	};
}
function transformPath(sx, sy, tx, ty, p) {
	const q = new Path();
	q.fill = p.fill;
	q.stroke = p.stroke;
	q.strokeWidth = p.strokeWidth;
	q.commands = p.commands.map((pc) => {
		switch (pc.type) {
			case "Z": return { type: "Z" };
			case "M":
			case "L": return {
				type: pc.type,
				x: pc.x * sx + tx,
				y: pc.y * sy + ty
			};
			case "Q": return {
				type: "Q",
				x: pc.x * sx + tx,
				y: pc.y * sy + ty,
				x1: pc.x1 * sx + tx,
				y1: pc.y1 * sy + ty
			};
			case "C": return {
				type: "C",
				x: pc.x * sx + tx,
				y: pc.y * sy + ty,
				x1: pc.x1 * sx + tx,
				y1: pc.y1 * sy + ty,
				x2: pc.x2 * sx + tx,
				y2: pc.y2 * sy + ty
			};
		}
	});
	return q;
}
/**
* A tiny transform matrix implementation that only supports scale and
* translation.
*/
var AlignedTransform = class {
	constructor(sx, sy, tx, ty) {
		this.sx = sx;
		this.sy = sy;
		this.tx = tx;
		this.ty = ty;
	}
	/**
	* Apply this transformation to all vertices and control points in an
	* opentype.js `Path`.
	*/
	transformPath(p) {
		return transformPath(this.sx, this.sy, this.tx, this.ty, p);
	}
};
//#endregion
//#region src/vdom.ts
function newElement(tag, attributes, children) {
	return {
		tag,
		attributes: attributes || {},
		children: children || []
	};
}
function isElement(x) {
	if (x != null && typeof x === "object" && "tag" in x && "attributes" in x && "children" in x) return typeof x.tag === "string" && x.attributes != null && typeof x.attributes === "object" && Array.isArray(x.children);
	return false;
}
function isElementWithTag(x, tag) {
	return isElement(x) && x.tag === tag;
}
function entityEscape(s) {
	return s.replace(/[&<>"']/g, (c) => "&#" + c.charCodeAt(0) + ";");
}
const ATTR_SUBSTITUTIONS = {
	className: "class",
	htmlFor: "for"
};
function renderAttribute(attrName, value) {
	if (value == null || value === false) return "";
	const resName = ATTR_SUBSTITUTIONS[attrName] || attrName;
	if (value === true) return ` ${resName}`;
	return ` ${resName}="${entityEscape(`${value}`)}"`;
}
const ATTRIBUTE_ORDER_OVERRIDE = [
	"id",
	"className",
	"class",
	"x",
	"y",
	"width",
	"height"
];
function attrNameSortKey(attrName) {
	if (attrName === "xmlns" || attrName.startsWith("xmlns:")) return `99${attrName}`;
	const overrideIndex = ATTRIBUTE_ORDER_OVERRIDE.indexOf(attrName);
	if (overrideIndex >= 0) return `${`${overrideIndex}`.padStart(2, "0")}${attrName}`;
	return `98${attrName}`;
}
function orderAttrPairs([aName, aValue], [bName, bValue]) {
	const aKey = attrNameSortKey(aName) + ` ${aValue}`;
	const bKey = attrNameSortKey(bName) + ` ${bValue}`;
	return aKey.localeCompare(bKey);
}
function renderElement(element, options) {
	const allowSelfClose = !!(options === null || options === void 0 ? void 0 : options.allowSelfClosingTags);
	const pretty = !!(options === null || options === void 0 ? void 0 : options.pretty);
	const sortAttrs = !!(options === null || options === void 0 ? void 0 : options.sortAttributes);
	const newline = pretty ? "\n" : "";
	const markup = [];
	function renderElement1(el, depth) {
		const indent = pretty ? " ".repeat(depth) : "";
		let closerIndex = -1;
		if (el.tag) {
			if (el.tag === "#comment") {
				const text = el.attributes["#text"];
				if (text) markup.push(indent, `<!-- ${text} -->`, newline);
				return;
			}
			markup.push(indent, `<${el.tag}`);
			const attrPairs = Object.entries(el.attributes);
			if (sortAttrs) attrPairs.sort(orderAttrPairs);
			for (let [attrName, attrValue] of attrPairs) markup.push(renderAttribute(attrName, attrValue));
			closerIndex = markup.length;
			markup.push(">", newline);
		}
		const lengthBeforeChildren = markup.length;
		for (let child of el.children) if (child != null) switch (typeof child) {
			case "string":
			case "number":
				{
					const text = entityEscape(`${child}`);
					if (text) markup.push(text);
				}
				break;
			default: renderElement1(child, depth + 1);
		}
		if (el.tag) if (allowSelfClose && lengthBeforeChildren === markup.length && closerIndex >= 0) markup[closerIndex] = " />";
		else {
			markup.push(indent, `</${el.tag}>`);
			if (depth > 0) markup.push(newline);
		}
	}
	renderElement1(element, 0);
	return markup.join("");
}
//#endregion
//#region src/version.ts
const VERSION = "0.2.2";
//#endregion
//#region src/word-break.ts
/**
* Create an array that maps character indices to word indices.
*
* The resulting array can be used to determine the word index of any
* character through non-monotonic/random access.
*
* The `allowNonMembership` parameter allows for -1 to appear in the
* returned array, which indicates a character is not part of a word. This
* would apply to space characters and newlines. For backwards
* compatibility, this may be set to false, which will ensure a non-negative
* index for all characters. In this mode, space characters are assigned to
* the most recent word index, except for leading space which will be
* assigned to word index 0.
*
* @param text The text used to produce `layout`.
* @param layout The `layout` as produced by `opentype-layout`.
* @param allowNonMembership Whether to allow -1 in the returned array.
*/
function makeWordMap(text, layout, allowNonMembership) {
	const charToWordIndex = [];
	let lineIndex = 0;
	let cueNewWord = true;
	let wordIndex = -1;
	for (let chIndex = 0; chIndex < text.length; chIndex++) {
		const nonWordValue = allowNonMembership ? -1 : Math.max(0, wordIndex);
		if (lineIndex >= layout.lines.length) {
			charToWordIndex.push(nonWordValue);
			continue;
		}
		const line = layout.lines[lineIndex];
		if (!line || chIndex < line.start) {
			charToWordIndex.push(nonWordValue);
			continue;
		}
		if (chIndex >= line.end) {
			lineIndex++;
			cueNewWord = true;
			chIndex--;
			continue;
		}
		if (/\s/.test(text.charAt(chIndex))) {
			charToWordIndex.push(nonWordValue);
			cueNewWord = true;
			continue;
		}
		if (cueNewWord) {
			cueNewWord = false;
			wordIndex++;
		}
		charToWordIndex.push(wordIndex);
	}
	return charToWordIndex;
}
//#endregion
//#region src/generate-svg.ts
const DECIMAL_PLACES_FOR_NON_PATHS = 8;
function generateMarkup(inputText, font, customStyles, options) {
	var _options$decimalPlace, _styles$stroke$width$, _styles$stroke;
	let decimalPlaces = (_options$decimalPlace = options === null || options === void 0 ? void 0 : options.decimalPlaces) !== null && _options$decimalPlace !== void 0 ? _options$decimalPlace : 5;
	if (decimalPlaces < 0) decimalPlaces = 0;
	const sizeReference = (options === null || options === void 0 ? void 0 : options.sizeReference) || void 0;
	const uidFunction = (options === null || options === void 0 ? void 0 : options.uidFunction) || randomUID;
	const styles = resolveStyles(customStyles || {}, font, sizeReference || void 0);
	const unitSystem = new UnitSystem(font.unitsPerEm, styles.fontSize.value);
	const normText = normalizeWhitespace(inputText);
	const layout = computeLayoutWithUnits(font, normText, {
		unitSystem,
		letterSpacing: styles.letterSpacing,
		lineHeight: styles.lineHeight,
		width: styles.width,
		align: styles.textAlign
	});
	const svgEl = createSvgDocument(normText, layout, decimalPlaces, styles.maskRows, unitSystem.getFactor("fu", "px"), (_styles$stroke$width$ = (_styles$stroke = styles.stroke) === null || _styles$stroke === void 0 ? void 0 : _styles$stroke.width.value) !== null && _styles$stroke$width$ !== void 0 ? _styles$stroke$width$ : 0, uidFunction);
	const topGroupEl = svgEl.children.find((el) => isElementWithTag(el, "g"));
	if (!topGroupEl) throw new Error(`Assert: topGroupEl=${topGroupEl}`);
	const layoutHeight = unitSystem.toPixels(layout.height + layout.baseline, "fu");
	const layoutWidth = unitSystem.toPixels(layout.width, "fu");
	const svgHeight = styles.height || layoutHeight;
	const svgWidth = styles.width || layoutWidth;
	const [translateX, translateY] = resolveTranslate(unitSystem.toPixels2D(styles.offset), styles.verticalAlign, svgHeight, layoutHeight);
	topGroupEl.attributes.transform = transformTranslate(translateX.value, translateY.value);
	Object.assign(topGroupEl.attributes, deriveTopGroupAttributes(styles));
	const svgStyles = deriveSvgCssStyles(styles);
	svgEl.attributes.style = inlineStylesAttr(svgStyles, true) || void 0;
	Object.assign(svgEl.attributes, deriveSvgAttributes(svgWidth, svgHeight, !!(options === null || options === void 0 ? void 0 : options.withViewBox)));
	return renderElement(svgEl, {
		pretty: true,
		allowSelfClosingTags: true,
		sortAttributes: true
	});
}
function createSvgDocument(text, layout, decimalPlaces, createRowMasks, fontUnitToPixelScale, strokeWidthInPixels, uidFunction) {
	const defsEl = newElement("defs");
	const topGroupEl = newElement("g", { className: "text-container" });
	const charToWordIndexMap = makeWordMap(text, layout, false);
	const wordContainers = /* @__PURE__ */ new Map();
	const positionedGlyphsPerRow = groupGlyphsByRow(layout.glyphs);
	const glyphTransform = new AlignedTransform(fontUnitToPixelScale, -fontUnitToPixelScale, 0, 0);
	for (let [rowIndex, glyphsInRow] of positionedGlyphsPerRow.entries()) {
		const rowIndexOneBased = rowIndex + 1;
		let rowBbox = null;
		let clipId = "";
		let rowClipRect = null;
		if (createRowMasks) {
			rowBbox = new BoundingBox();
			const rowUid = uidFunction();
			const rectId = `rm${rowUid}`;
			clipId = `rowmask${rowUid}`;
			rowClipRect = newElement("rect", { className: `${rectId} rowmask` });
			const clipPath = newElement("clipPath", { id: clipId }, [rowClipRect]);
			defsEl.children.push(clipPath);
		}
		const rowGroup = newElement("g", { className: `row r${rowIndexOneBased}` });
		const rowClipper = newElement("g", {
			className: `rc rc${rowIndexOneBased}`,
			"clip-path": clipId ? `url(#${clipId})` : void 0
		}, [rowGroup]);
		topGroupEl.children.push(rowClipper);
		for (let glyphMeta of glyphsInRow) {
			const glyph = glyphMeta.data;
			if (glyph.name === "space" || glyph.path.commands.length < 1) continue;
			glyphTransform.tx = glyphMeta.position[0] * fontUnitToPixelScale;
			glyphTransform.ty = glyphMeta.position[1] * -fontUnitToPixelScale;
			const pathInPxSpace = glyphTransform.transformPath(glyph.path);
			const svgPathData = pathInPxSpace.toPathData(decimalPlaces);
			if (!svgPathData) continue;
			const pathEl = newElement("path", {
				className: "letter",
				d: minifyNumbers(svgPathData)
			});
			if (rowBbox) boundingBoxUnion(rowBbox, pathInPxSpace.getBoundingBox());
			const wordIndex = charToWordIndexMap[glyphMeta.index];
			if (wordIndex != null && wordIndex >= 0) {
				let wordEl = wordContainers.get(wordIndex);
				if (!wordEl) {
					wordEl = newElement("g", { className: `word w${wordIndex + 1}` });
					wordContainers.set(wordIndex, wordEl);
					rowGroup.children.push(wordEl);
				}
				wordEl.children.push(pathEl);
			} else rowGroup.children.push(pathEl);
		}
		if (rowBbox && !rowBbox.isEmpty()) {
			const halfStroke = strokeWidthInPixels / 2;
			boundingBoxOutset(rowBbox, halfStroke + 1);
			boundingBoxRoundOutset(rowBbox);
			if (rowClipRect) Object.assign(rowClipRect.attributes, boundingBoxGetXYWH(rowBbox));
			else throw new Error(`Assert: rowClipRect is ${rowClipRect}`);
		}
	}
	for (const wordEl of wordContainers.values()) {
		let letterIndexOneBased = 1;
		for (const childEl of wordEl.children) if (childEl && typeof childEl === "object" && childEl.attributes.className === "letter") {
			childEl.attributes.className += ` l${letterIndexOneBased}`;
			letterIndexOneBased++;
		}
	}
	const commentEl = newElement("#comment", { "#text": `v${VERSION}` });
	const svgEl = newElement("svg", { xmlns: XMLNS_SVG }, [commentEl]);
	if (defsEl.children.length > 0) svgEl.children.push(defsEl);
	svgEl.children.push(topGroupEl);
	return svgEl;
}
function groupGlyphsByRow(positionedGlyphs) {
	const rows = [];
	for (const pg of positionedGlyphs) {
		while (pg.row >= rows.length) rows.push([]);
		rows[pg.row].push(pg);
	}
	return rows;
}
function resolveTranslate(inputTranslate, verticalAlign, availableHeight, layoutHeight) {
	const [tx, ty] = inputTranslate;
	const spareHeight = availableHeight.value - layoutHeight.value;
	switch (verticalAlign) {
		case "top":
		default: break;
		case "middle":
			ty.value += spareHeight / 2;
			break;
		case "bottom":
			ty.value += spareHeight;
			break;
	}
	return [tx, ty];
}
function transformTranslate(x, y) {
	return minifyNumbers(`translate(${x.toFixed(DECIMAL_PLACES_FOR_NON_PATHS)},${y.toFixed(DECIMAL_PLACES_FOR_NON_PATHS)})`);
}
function formatFixedUnitValue(unitValue) {
	return `${minifyNumbers(unitValue.value.toFixed(DECIMAL_PLACES_FOR_NON_PATHS))}${unitValue.unit}`;
}
function deriveSvgCssStyles({ position, boundingBox, dropshadow }) {
	const svgStyles = {};
	if (position) {
		const { left, top } = position;
		svgStyles.position = "absolute";
		if (left) svgStyles.left = formatFixedUnitValue(left);
		if (top) svgStyles.top = formatFixedUnitValue(top);
	}
	if (boundingBox) svgStyles.border = boundingBox;
	if (dropshadow) {
		const { color, blur, offset } = dropshadow;
		svgStyles.filter = `drop-shadow(${[...offset, blur].map((uv) => formatFixedUnitValue(uv)).join(" ")} ${color})`;
	}
	return svgStyles;
}
function deriveSvgAttributes(widthPx, heightPx, withViewBox) {
	const width = Math.max(1, Math.floor(widthPx.value));
	const height = Math.max(1, Math.floor(heightPx.value));
	return {
		width,
		height,
		viewBox: withViewBox ? `0 0 ${width} ${height}` : void 0
	};
}
function deriveTopGroupAttributes({ color, stroke }) {
	const attrs = { fill: color };
	if (stroke) {
		const { color, opacity, width, linecap } = stroke;
		Object.assign(attrs, {
			stroke: color,
			"stroke-opacity": opacity,
			"stroke-width": width.value,
			"stroke-linecap": linecap
		});
	}
	return attrs;
}
function randomUID() {
	const n = Math.random() * Number.MAX_SAFE_INTEGER;
	return Math.floor(n).toString(36).padStart(11, "0");
}
//#endregion
//#region src/index.ts
/**
* Uses a Font to render some text as `<path>` elements inside an `<svg>`
* element. Returns the markup as a string.
*
* @param text The text to render.
* @param font An `opentype.js` `Font` object, typically acquired through
*  the functions `load`, `loadSync`, or `parse`.
* @param styles The styles to apply.
* @param options Options controlling markup.
* @returns The markup as a string.
*/
function svgMarkup(text, font, styles, options) {
	return generateMarkup(text, font, styles, options);
}
/**
* Loads a font from a URL, then swaps the provided container with an
* `<svg>` element showing the rendered text.
*
* The content of `container` is replaced by setting its `innerHTML`, and
* the resulting `SVGElement` is returned.
*
* @param container The HTMLElement to replace.
* @param text The text to render.
* @param fontURL The URL of a font file to load and parse.
* @param styles The styles to apply.
* @param decimalPlaces The number of fractional decimal places used to
*   render `<path d>` attributes. This is equivalent to
*   `options.decimalPlaces` when calling `svgMarkup`.
* @returns A Promise which resolves to the resulting `SVGElement`.
*/
async function makeSVG(container, text, fontURL, styles, decimalPlaces) {
	container.innerHTML = generateMarkup(text, await opentype.load(fontURL), styles, {
		sizeReference: container,
		decimalPlaces
	});
	const svgElement = container.firstElementChild;
	if (!svgElement || !(svgElement instanceof SVGElement)) throw new Error(`Assert: Not an SVGElement: ${svgElement === null || svgElement === void 0 ? void 0 : svgElement.tagName}`);
	for (const attrToCopy of ["id", "class"]) {
		const attrValue = container.getAttribute(attrToCopy);
		if (attrValue) {
			container.removeAttribute(attrToCopy);
			svgElement.setAttribute(attrToCopy, attrValue);
		}
	}
	if (container.parentNode) container.replaceWith(svgElement);
	else svgElement.remove();
	return svgElement;
}
/**
* Uses a Font to render some text as `<path>` elements inside an `<svg>`
* element. Returns the markup as a string.
*
* This is a legacy equivalent of `svgMarkup`. Using that function
* offers more control over font loading and exposes more options.
*
* @param text The text to render.
* @param fontRef A URL, or an `ArrayBuffer` containing the contents of a
*   font file that `opentype.js` can parse. If this is a URL, it is
*   passed to `opentype.js#load` and awaited.
* @param styles The styles to apply.
* @param decimalPlaces The number of fractional decimal places used to
*   render `<path d>` attributes. This is equivalent to
*   `options.decimalPlaces` when calling `svgMarkup`.
* @returns A Promise which resolves to the markup as a string.
*/
async function svgData(text, fontRef, styles, decimalPlaces) {
	return svgMarkup(text, typeof fontRef === "string" ? await opentype.load(fontRef) : opentype.parse(fontRef), styles, { decimalPlaces });
}
/**
* Uses a Font to render some text as <path> elements inside an <svg>
* element. Returns the markup as a string.
*
* This is a legacy equivalent of `svgMarkup`. Using that function
* offers more control over font parsing and exposes more options.
*
* @param text The text to render.
* @param fontRef An `ArrayBuffer` containing the contents of a
*   font file that `opentype.js` can parse.
* @param styles The styles to apply.
* @param decimalPlaces The number of fractional decimal places used to
*   render `<path d>` attributes. This is equivalent to
*   `options.decimalPlaces` when calling `svgMarkup`.
* @returns The markup as a string.
*/
function svgDataSync(text, fontRef, styles, decimalPlaces) {
	return svgMarkup(text, opentype.parse(fontRef), styles, { decimalPlaces });
}
var src_default = {
	svgMarkup,
	makeSVG,
	svgData,
	svgDataSync
};
//#endregion
export { src_default as default, makeSVG, svgData, svgDataSync, svgMarkup };
